import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/auth';
import { useApp } from 'contexts/app';
import { allMenu } from './config';
import ChatService from 'services/chat';
import NotificationService from 'services/notification';
import { io } from 'socket.io-client';
import { socket_url } from 'config';

const SOCKET_OPTIONS = {
  transports: ['websocket', 'polling'],
  reconnection: true,
  reconnectionAttempts: Infinity,
  reconnectionDelay: 1000,
  reconnectionDelayMax: 8000,
  timeout: 20000,
};

const useMenu = ({
  showMenu = false,
  onCloseMenu,
  accountMenu,
  workspaceName,
  userFirstName,
} = {}) => {
  const [menuItems, setMenuItems] = useState([]);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const [notificationUnreadCount, setNotificationUnreadCount] = useState(0);
  const navigate = useNavigate();
  const { showNotify } = useApp();
  const { user } = useAuth();
  const socketRef = useRef(null);
  const serviceRef = useRef(new ChatService());
  const notificationServiceRef = useRef(new NotificationService());

  const refreshChatUnread = useCallback(async () => {
    try {
      const threads = await serviceRef.current.listThreads({ page: 1, page_size: 50 });
      const total = (threads || []).reduce((sum, item) => sum + Number(item?.unread_shop || 0), 0);
      setChatUnreadCount(total);
    } catch (error) {
      setChatUnreadCount(0);
    }
  }, []);

  const refreshNotificationUnread = useCallback(async () => {
    try {
      const total = await notificationServiceRef.current.countUnread();
      setNotificationUnreadCount(Number(total || 0));
    } catch (error) {
      setNotificationUnreadCount(0);
    }
  }, []);

  useEffect(() => {
    refreshChatUnread();
    refreshNotificationUnread();
  }, [refreshChatUnread, refreshNotificationUnread]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.dispatchEvent(new CustomEvent('notification-unread-updated', {
      detail: { count: notificationUnreadCount },
    }));
  }, [notificationUnreadCount]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      return;
    }

    const socket = io(socket_url, {
      ...SOCKET_OPTIONS,
      auth: { token },
    });

    socketRef.current = socket;
    socket.on('chat:thread_updated', () => {
      refreshChatUnread();
    });
    socket.on('notification:new', (payload = {}) => {
      refreshNotificationUnread();
      showNotify({
        message: payload?.title || 'Thông báo mới',
        description: payload?.message || 'Có cập nhật mới cần xử lý.',
      }, 'info');
    });
    socket.on('notification:updated', (payload = {}) => {
      if (typeof payload?.unread_count === 'number') {
        setNotificationUnreadCount(payload.unread_count);
        return;
      }
      refreshNotificationUnread();
    });
    socket.on('reconnect', () => {
      refreshChatUnread();
      refreshNotificationUnread();
    });
    socket.on('connect_error', () => {
      // socket.io will retry automatically
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [refreshChatUnread, refreshNotificationUnread, showNotify]);

  const menuTemplate = useMemo(() => {
    return allMenu.map((item) => {
      if (item.key === '/chats') {
        return {
          ...item,
          badgeCount: chatUnreadCount,
        };
      }

      if (item.key === '/notifications') {
        return {
          ...item,
          badgeCount: notificationUnreadCount,
        };
      }

      if (item.key !== '/chats' && item.key !== '/notifications') {
        return item;
      }

      return item;
    });
  }, [chatUnreadCount, notificationUnreadCount]);

  useEffect(() => {
    const wp = user.work_space;
    const permissions = wp.permissions ?? [];

    let items = [...menuTemplate];
    if (!['admin', 'owner'].includes(wp.role)) {
      items = items.filter((menu) => {
        if (!menu.permission) {
          return true;
        }

        if (permissions.includes(menu.permission)) {
          return true;
        }

        console.log('wp', wp);
        if (wp.role === menu.role) {
          return true;
        }

        return false;
      });
    }

    setMenuItems(items);
  }, [user, menuTemplate]);

  const menuClick = useCallback((e) => {
    if (onCloseMenu) {
      onCloseMenu();
    }
    navigate(e.key);
  }, [navigate, onCloseMenu]);

  return {
    menuItems,
    menuClick,
    showMenu,
    onCloseMenu,
    accountMenu,
    workspaceName,
    userFirstName,
  };
};

export default useMenu;
